<template>
    <div>
        <div v-if="!downloadMode">
            <b-row>
                <!-- CSV 내보내기 (일반 모드일 때만 표시) -->
                <b-col :md="12" :sm="6" class="mb-50">
                    <b-button
                        variant="success"
                        :disabled="disabledValue || !availableExport"
                        @click="exportCsv"
                        block
                    >
                        <feather-icon
                            icon="DownloadIcon"
                        />
                        {{ $t('v4.Export CSV') }}
                    </b-button>
                </b-col>
                <!-- 필터 관리 (일반 모드일 때만 표시) -->
                <b-col :md="12" :sm="6">
                    <b-dropdown
                        v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                        :disabled="disabledValue"
                        :text="$t('v4.Manage Filters')"
                        variant="outline-secondary"
                        right
                        block
                    >
                        <b-dropdown-item
                            v-b-toggle.add-filter
                        >
                            <i class="fas fa-plus fa-fw mr-50"></i>
                            {{ $t('v4.Save current') }}
                        </b-dropdown-item>
                        <b-dropdown-item
                            v-b-toggle.delete-filter
                        >
                            <i class="fas fa-times fa-fw mr-50"></i>
                            {{ $t('v4.Delete') }}
                        </b-dropdown-item>
                    </b-dropdown>
                </b-col>
            </b-row>
        </div>
        <div v-else>
            <!-- 다운로드 모드일 때 표시 -->
            <b-row>
                <!-- 사용 가이드 버튼 -->
                <b-col :md="12" :sm="6" class="mb-50">
                    <b-button
                        variant="warning"
                        @click="openUsageGuide"
                        block
                    >
                        <feather-icon
                            icon="HelpCircleIcon"
                        />
                        {{ $t('v4.Usage Guide') }}
                    </b-button>
                </b-col>
                <!-- 인코딩변환가이드 버튼 (영어가 아닐 때만 표시) -->
                <b-col :md="12" :sm="6" v-if="locale !== 'en'">
                    <b-button
                        variant="outline-secondary"
                        @click="openEncodingGuide"
                        block
                    >
                        <feather-icon
                            icon="FileTextIcon"
                        />
                        {{ $t('v4.Encoding Guide') }}
                    </b-button>
                </b-col>
            </b-row>
        </div>

        <!-- 사용 가이드 모달 -->
        <b-modal
            v-model="showUsageGuideModal"
            :title="$t('v4.Usage Guide')"
            size="lg"
            hide-footer
            scrollable
        >
            <div class="p-2">
                <h5 class="mb-3">{{ $t('v4.Download Mode Usage Guide') }}</h5>

                <div class="mb-4">
                    <h6 class="text-primary mb-2">
                        <feather-icon icon="InfoIcon" size="16" class="mr-1" />
                        {{ $t('v4.What is Download Mode') }}
                    </h6>
                    <p class="text-muted">
                        {{ $t('v4.Download Mode Description') }}
                    </p>
                </div>

                <div class="mb-4">
                    <h6 class="text-success mb-2">
                        <feather-icon icon="TrendingUpIcon" size="16" class="mr-1" />
                        {{ $t('v4.When to Use') }}
                    </h6>
                    <ul class="pl-3 text-muted">
                        <li class="mb-2">{{ $t('v4.When Large Data') }}</li>
                        <li class="mb-2">{{ $t('v4.When Full Export') }}</li>
                        <li class="mb-2">{{ $t('v4.When Performance Issue') }}</li>
                    </ul>
                </div>

                <div class="mb-4">
                    <h6 class="text-primary mb-2">
                        <feather-icon icon="ListIcon" size="16" class="mr-1" />
                        {{ $t('v4.How to Use') }}
                    </h6>
                    <ol class="pl-3">
                        <li class="mb-2">{{ $t('v4.Step 1 Enable Mode') }}</li>
                        <li class="mb-2">{{ $t('v4.Step 2 Select Account') }}</li>
                        <li class="mb-2">{{ $t('v4.Step 3 Select Period') }}</li>
                        <li class="mb-2">{{ $t('v4.Step 4 Request Download') }}</li>
                        <li class="mb-2">{{ $t('v4.Step 5 Download File') }}</li>
                    </ol>
                </div>

                <div class="mb-4">
                    <h6 class="text-info mb-2">
                        <feather-icon icon="InfoIcon" size="16" class="mr-1" />
                        {{ $t('v4.Key Features') }}
                    </h6>
                    <ul class="pl-3 text-muted">
                        <li class="mb-2">{{ $t('v4.Feature No Filter') }}</li>
                        <li class="mb-2">{{ $t('v4.Feature Field Reset') }}</li>
                        <li class="mb-2">{{ $t('v4.Feature No API Call') }}</li>
                        <li class="mb-2">{{ $t('v4.Feature Async Process') }}</li>
                    </ul>
                </div>

                <div class="mb-4">
                    <h6 class="text-warning mb-2">
                        <feather-icon icon="AlertCircleIcon" size="16" class="mr-1" />
                        {{ $t('v4.Important Notes') }}
                    </h6>
                    <ul class="pl-3 text-muted">
                        <li class="mb-2">{{ $t('v4.Note Mode Switch') }}</li>
                        <li class="mb-2">{{ $t('v4.Note Processing Time') }}</li>
                        <li class="mb-2">{{ $t('v4.Note Data Size') }}</li>
                        <li class="mb-2">{{ $t('v4.Note File Format') }}</li>
                    </ul>
                </div>

                <div class="text-center mt-4">
                    <b-button
                        variant="primary"
                        @click="showUsageGuideModal = false"
                    >
                        {{ $t('v4.Confirm') }}
                    </b-button>
                </div>
            </div>
        </b-modal>

        <b-sidebar
            id="add-filter"
            bg-variant="white"
            left
            backdrop
            shadow
            :title="$t('v4.Save Filter')"
            width="400px"
        >
            <template #default="{ hide }">
                <b-form class="p-1" @submit.stop.prevent>
                    <b-form-row>
                        <b-col
                            md="12"
                            class="mb-1"
                        >
                            <label for="input-invalid2">
                                {{ $t('v4.Filter Name') }}
                            </label>
                            <b-form-input
                                v-model="filterName"
                                id="basicInput"
                                :placeholder="$t('v4.Enter name')"
                            />
                            <p class="text-muted mt-25 mb-0">
                                <small>- {{$t('org.billing.Saves current items and filtering values')}}</small>
                            </p>
                            <p class="text-muted mt-0 mb-0">
                                <small>- {{$t('org.billing.For AWS Account / Group, items are saved but filters are not')}}</small>
                            </p>
                        </b-col>
                        <b-col
                            md="12"
                        >
                            <b-button
                                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                                type="button"
                                variant="primary"
                                @click="createFilter(hide)"
                            >
                                {{ $t('org.common.Save') }}
                            </b-button>
                        </b-col>
                    </b-form-row>
                </b-form>
            </template>
        </b-sidebar>
        <b-sidebar
            id="delete-filter"
            bg-variant="white"
            left
            backdrop
            shadow
            :title="$t('v4.Delete Filters')"
        >
            <template #default="{ hide }">
                <b-form class="p-1">
                    <b-form-row>
                        <b-col
                            md="12"
                            class="mb-1"
                        >
                            <b-form-group
                                :label="$t('v4.Select filters you want to delete')"
                                v-slot="{ ariaDescribedby }"
                            >
                                <b-form-checkbox-group
                                    v-model="selected"
                                    :options="options"
                                    :aria-describedby="ariaDescribedby"
                                    name="filters"
                                    stacked
                                />
                            </b-form-group>
                        </b-col>
                        <b-col
                            md="12"
                        >
                            <b-button
                                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                                type="button"
                                variant="danger"
                                @click="deleteFilter(hide)"
                            >
                                {{ $t('v4.Submit') }}
                            </b-button>
                        </b-col>
                    </b-form-row>
                </b-form>
            </template>
        </b-sidebar>
    </div>
</template>

<script>
import useCostPivot from '@/composables/useCostPivot'
import Ripple from 'vue-ripple-directive'
import { toRefs, computed, ref, getCurrentInstance } from '@vue/composition-api'
export default {
    model: {
        prop: 'fieldStatus',
        event: 'more:fields'
    },
    props: {
        items: {
            type: Object,
            default: function () {
                return {
                    rows: ['usageAccountId'],
                    cols: ['ym'],
                    compositSources: ['usageAccountId', 'ym'],
                    value: 'unblendedCost'
                }
            }
        },
        filters: {
            type: Array,
            default: function () {
                return []
            }
        },
        fieldStatus: {
            type: Object,
            default: function () {
                return {
                    description: false,
                    resource: false
                }
            }
        },
        loading: {
            type: Boolean,
            default: false
        },
        availableExport: {
            type: Boolean,
            default: true
        },
        downloadMode: {
            type: Boolean,
            default: false
        },
        noData: {
            type: Boolean,
            default: false
        }
    },
    directives: {
        Ripple
    },
    setup (props, context) {
        const vm = getCurrentInstance().proxy
        const { loading, filters, items, noData } = toRefs(props)
        const selected = ref([])
        const filterName = ref('')
        const showUsageGuideModal = ref(false)
        const { attributeDescriptionItems, attributeResourceItems, getCompositSources } = useCostPivot()
        const options = computed(() => filters.value.map(item => ({ text: item.name, value: item.costPivotFilterIdx })))
        const namespace = process.env.VUE_APP_NAMESPACE
        const locale = computed(() => vm.$t('org.locale'))
        const exportCsv = () => {
            context.emit('export:csv')
        }
        const openUsageGuide = () => {
            // 사용 가이드 모달 열기
            showUsageGuideModal.value = true
        }
        const openEncodingGuide = () => {
            // 인코딩 변환 가이드 URL 열기 - 기존 링크 사용
            const encodingGuideUrl = `/user-guide?path=docs%2F${namespace}%2F${locale.value}%2Fcsv-encoding%2Fdocument.json`
            window.open(encodingGuideUrl, '_blank')
        }

        const includesCustomField = (currentSources, customFields) => {
            const keys = customFields.map(item => item.key)
            return currentSources.some(source => keys.includes(source))
        }
        const currentSources = computed(() => getCompositSources(items.value.rows, items.value.cols))
        const isDescriptionFields = computed(() => includesCustomField(currentSources.value, attributeDescriptionItems))
        const isResourceFields = computed(() => includesCustomField(currentSources.value, attributeResourceItems))
        // const descriptions = currentSources.value.filter(item => attributeDescriptionItems.includes(item))
        const disabledValue = computed(() => (loading.value || noData.value) ? 'disabled' : undefined)
        const createFilter = (hide) => {
            context.emit('create:filter', { name: filterName.value })
            filterName.value = ''
            hide()
        }
        const deleteFilter = (hide) => {
            context.emit('delete:filter', { values: selected.value })
            selected.value = []
            hide()
        }
        return {
            currentSources,
            attributeDescriptionItems,
            attributeResourceItems,
            options,
            selected,
            filterName,
            showUsageGuideModal,
            disabledValue,
            locale,
            exportCsv,
            openUsageGuide,
            openEncodingGuide,
            createFilter,
            deleteFilter,
            isDescriptionFields,
            isResourceFields
        }
    }
}
</script>

<style>

</style>
